// src/components/Layout/Breadcrumbs.jsx
import React from "react";
import styled from "styled-components";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import MUIBreadcrumbs from "@material-ui/core/Breadcrumbs";
import { generateUrlForWorkspace } from "../Workspace/common";

/* ──────────────────────────────────────────────
   ROUTES (put first): regex -> { title(ctx), crumb?(ctx) }
   - title: REQUIRED function returning the page title string
   - crumb: OPTIONAL function returning an array of { label, to }
   ctx = { pathname, workspaces, currentMonitoredUri }
   ────────────────────────────────────────────── */
const ROUTES = [
    {
        re: /^\/workspaces$/,
        title: () => "All workspaces",
        crumb: () => [{ label: "All Workspaces", to: "/workspaces" }],
    },
    {
        re: /^\/account$/,
        title: () => "Account",
    },
    {
        // Workspace dashboard — include workspace name in crumb
        re: /^\/ws\/\d+(?:\/dashboard)?$/,
        title: () => "Dashboard",
        crumb: ({ pathname, workspaces }) => {
            const ws = parseWorkspacePath(pathname);
            const wsRoot = ws?.wsRoot || "/workspaces";
            const wsNum = ws?.wsNum;
            const wsName = workspaceNameFromState(workspaces, wsNum);
            return [
                { label: "All Workspaces", to: "/workspaces" },
                { label: wsName, to: wsRoot },
                { label: "Dashboard", to: wsRoot }, // last crumb is rendered as current
            ];
        },
    },
    { re: /^\/ws\/\d+\/broken-links$/, title: () => "Broken Links" },
    { re: /^\/ws\/\d+\/add-url$/, title: () => "Add Recommendations" },
    { re: /^\/ws\/\d+\/admin$/, title: () => "Workspace Settings" },
    { re: /^\/ws\/\d+\/issues\/[^/]+$/, title: () => "Issue Deep Dive" },
    {
        // Monitored URI detail
        re: /^\/ws\/\d+\/monitored-uris\/([^/]+)$/,
        title: () => "URL Details",
        crumb: ({ pathname, workspaces, currentMonitoredUri }) => {
            const wsNum = currentMonitoredUri?.workspaceNumber
                || pathname.match(/^\/ws\/(\d+)\//)?.[1];
            const wsName = workspaceNameFromState(workspaces, wsNum);
            const wsRoot = wsNum ? generateUrlForWorkspace(wsNum) : "/workspaces";
            return [
                { label: "All Workspaces", to: "/workspaces" },
                { label: wsName, to: wsRoot },
                { label: "URL Details", to: pathname },
            ];
        },
    },
];

/* ──────────────────────────────────────────────
   Component
   ────────────────────────────────────────────── */
export default function BreadcrumbsBar() {
    const { crumbs, pageTitle } = useBreadcrumbModel();

    return (
        <Bar role="navigation" aria-label="Breadcrumb">
            <Inner>
                <Title>{pageTitle}</Title>

                <Crumbs separator="/" maxItems={5} itemsBeforeCollapse={2} itemsAfterCollapse={1}>
                    {crumbs.map((c, idx) =>
                        idx === crumbs.length - 1 ? (
                            <CrumbCurrent key={c.to} aria-current="page">
                                <CrumbText title={c.label}>{c.label}</CrumbText>
                            </CrumbCurrent>
                        ) : (
                            <CrumbLink key={c.to} to={c.to}>
                                <CrumbText title={c.label}>{c.label}</CrumbText>
                            </CrumbLink>
                        )
                    )}
                </Crumbs>
            </Inner>
            <Divider />
        </Bar>
    );
}

/* ──────────────────────────────────────────────
   Model hook
   ────────────────────────────────────────────── */
function useBreadcrumbModel() {
    const { pathname } = useLocation();
    const workspaces = useSelector((s) => s.workspaces);
    const currentMonitoredUri = useSelector((s) => s.currentMonitoredUri);

    const ctx = { pathname, workspaces, currentMonitoredUri };
    const rule = ROUTES.find((r) => r.re.test(pathname));

    // title is required (function)
    const explicitTitle = rule ? rule.title(ctx) : null;

    // crumbs: route override or default
    const crumbs =
        rule && rule.crumb
            ? rule.crumb(ctx)
            : buildDefaultCrumbs({ pathname, workspaces, explicitTitle });

    const pageTitle = explicitTitle || (crumbs.length ? crumbs[crumbs.length - 1].label : " ");

    return { crumbs, pageTitle };
}

/* ──────────────────────────────────────────────
   Styles
   ────────────────────────────────────────────── */
const Bar = styled.div`
    background: transparent;
    padding-bottom: 20px;
`;
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 0 16px;
`;
const Title = styled.h1`
  margin: 0 0 6px 0;
  font-weight: 700;
  color: #111827;
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  line-height: 1.2;
`;
const Crumbs = styled(MUIBreadcrumbs)`
    border: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    &::before,
    &::after {
        display: none !important;
    }
`;
const Divider = styled.div`
    height: 1px;
    background: #e5e7eb;
    margin-top: 12px;
`;
const CrumbLink = styled(NavLink)`
    color: #2563eb;
    font-size: 0.9rem;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
const CrumbCurrent = styled.span`
    color: #6b7280;
    font-size: 0.9rem;
`;
const CrumbText = styled.span`
    display: inline-block;
    max-width: clamp(96px, 28vw, 320px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
`;

/* ──────────────────────────────────────────────
   Utilities (kept below per your preference)
   ────────────────────────────────────────────── */
const titleCase = (s) =>
    s.replace(/[-_]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

function parseWorkspacePath(pathname) {
    const m = pathname.match(/^\/ws\/(\d+)(?:\/(.*))?$/);
    if (!m) return null;
    const wsNum = m[1];
    const tail = (m[2] || "").replace(/\/+$/, "");
    const sections = tail.split("/").filter(Boolean);
    const wsRoot = generateUrlForWorkspace(wsNum);
    const isDashboard = sections.length === 0 || sections[0].toLowerCase() === "dashboard";
    return { wsNum, sections, wsRoot, isDashboard };
}

function workspaceNameFromState(workspaces, wsNum) {
    const cur = workspaces?.current;
    if (wsNum && String(cur?.seqNumber) === String(wsNum) && cur?.name) return cur.name;
    if (wsNum && Array.isArray(workspaces?.data)) {
        const found = workspaces.data.find((w) => String(w.seqNumber) === String(wsNum));
        if (found?.name) return found.name;
    }
    return "Workspace";
}

/* Default crumb builder (used when a route doesn't provide its own) */
function buildDefaultCrumbs({ pathname, workspaces, explicitTitle }) {
    if (pathname === "/workspaces") return [{ label: "All Workspaces", to: "/workspaces" }];
    if (pathname === "/account") return [{ label: "Account", to: "/account" }];

    const ws = parseWorkspacePath(pathname);
    if (ws) {
        const name = workspaceNameFromState(workspaces, ws.wsNum);

        // Dashboard: include workspace name in crumbs
        if (ws.isDashboard) {
            return [
                { label: "All Workspaces", to: "/workspaces" },
                { label: name, to: ws.wsRoot },
                { label: "Dashboard", to: ws.wsRoot },
            ];
        }

        // Sections: include workspace name, then tail segments
        const base = [
            { label: "All Workspaces", to: "/workspaces" },
            { label: name, to: ws.wsRoot },
        ];

        let acc = ws.wsRoot;
        const tail = ws.sections.map((seg, i, arr) => {
            acc += `/${seg}`;
            const isLast = i === arr.length - 1;
            return { label: isLast && explicitTitle ? explicitTitle : titleCase(seg), to: acc };
        });
        return [...base, ...tail];
    }

    // Generic fallback
    const parts = pathname.split("/").filter(Boolean);
    if (!parts.length) return [{ label: "All Workspaces", to: "/" }];

    let acc = "";
    return parts.map((seg, i) => {
        acc += `/${seg}`;
        const isLast = i === parts.length - 1;
        return { label: isLast && explicitTitle ? explicitTitle : titleCase(seg), to: acc };
    });
}
